import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import React, { useCallback, useMemo, useRef } from 'react';

import { AlphaSortSlider } from '@components/AlphaSortSlider/AlphaSortSlider';
import EmptyStateNoProjects from '@components/EmptyState/EmptyStateNoProjects';
import ProjectCard from '@components/ProjectCard/ProjectCard.web';
import { ProjectCardGrid } from '@components/ProjectList/ProjectCardGrid.web';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { PaddingBottomListFooter } from '@components/shared/ListSpacers';
import { Project } from '@graphql/generated';

export interface ProjectListProps {
  expanded: boolean;
  projects: Project[];
  onClick: (project: Project) => void;
  refreshControl?: React.JSX.Element;
  loading: boolean;
  listEmptyComponent?: () => React.JSX.Element;
  highlightedValue?: string;
  alphabetize?: boolean;
  fetchMore?: () => void;
  refreshing?: boolean;
  alphabetical?: boolean;
  onEndReachedThreshold?: number;
}

const sortProjects = (list: Project[]) => {
  const numsSpecials: Project[] = []; // numbers and ASCII Before A
  const letters: Project[] = []; // letters
  const asterisks: Project[] = []; //ASCII after z

  list.forEach((s) => {
    if (s.name) {
      if (s.name[0].match(/[a-z]/i)) {
        // check first letter
        letters.push(s);
      } else if (s.name[0].charCodeAt(0) < 65) {
        numsSpecials.push(s);
      } else {
        asterisks.push(s);
      }
    }
  });

  return { letters, numsSpecials, asterisks };
};

const ProjectListWebFooter: React.FC<{ hideMargin?: boolean }> = ({
  hideMargin,
}) => (
  <Box
    height={1}
    backgroundColor='grey02'
    marginHorizontal={hideMargin ? undefined : 'm'}
    marginBottom='listFooter'
  />
);

// Pure: turns the project array into the flat FlashList data (section headers
// interleaved with project rows) plus the alpha-slider rail. Extracted so it
// can be memoized in the component and unit-tested directly. (TA-10044)
export const buildAlphabetizedSections = (projects: Project[]) => {
  const { letters, numsSpecials, asterisks } = sortProjects(projects);

  const formattedAlpha: (Project | string)[] = [];
  const formattedNumSpecial: (Project | string)[] = [];
  const formattedAsterisk: (Project | string)[] = [];
  const alphabetizeList: string[] = [];

  if (numsSpecials.length > 0) {
    formattedNumSpecial.push('#');
    numsSpecials.forEach((obj) => {
      formattedNumSpecial.push(obj);
    });
  }
  alphabetizeList.push('#');
  letters.forEach((obj) => {
    const key = obj.name.charAt(0).toUpperCase();

    if (formattedAlpha.includes(key)) {
      formattedAlpha.push(obj);
    } else {
      formattedAlpha.push(key);
      formattedAlpha.push(obj);
    }
  });
  for (
    let charCode = 'A'.charCodeAt(0);
    charCode <= 'Z'.charCodeAt(0);
    charCode++
  ) {
    alphabetizeList.push(String.fromCharCode(charCode));
  }

  if (asterisks.length > 0) {
    formattedAsterisk.push('*');
    asterisks.forEach((obj) => {
      formattedAsterisk.push(obj);
    });
  }
  alphabetizeList.push('*');

  return {
    sectionData: [
      ...formattedNumSpecial,
      ...formattedAlpha,
      ...formattedAsterisk,
    ],
    alphabetizeList,
  };
};

const ProjectList = ({
  expanded,
  projects,
  onClick,
  refreshControl,
  loading,
  listEmptyComponent,
  highlightedValue,
  alphabetize,
  fetchMore,
  refreshing,
  alphabetical = true,
  onEndReachedThreshold = undefined,
}: ProjectListProps) => {
  const currentLetterRef = useRef('');
  const flashListRef = useRef<FlashList<string | Project>>(null);

  // Sorting + categorising the full project array is O(n) work that must not
  // re-run on unrelated state changes (hover, modal open). Keyed on `projects`
  // so it only recomputes when the list itself changes. Skipped entirely when
  // `alphabetize` is false (e.g. AllProjects) — that branch never renders, so
  // the result would only be computed and discarded. (TA-10044)
  const { sectionData, alphabetizeList } = useMemo(
    () =>
      alphabetize
        ? buildAlphabetizedSections(projects)
        : { sectionData: [], alphabetizeList: [] },
    [projects, alphabetize]
  );

  const getProjectCard = useCallback(
    (item: Project) => {
      return (
        <ProjectCard
          expanded={expanded}
          project={item}
          onClick={() => onClick(item)}
          filterVal={highlightedValue || ''}
          alphabetical={alphabetical}
          alphabetize={alphabetize}
        />
      );
    },
    [expanded, onClick, highlightedValue, alphabetical, alphabetize]
  );

  const getExpandedBox = useCallback(
    (item: Project) => {
      if (!alphabetize && !alphabetical) {
        return (
          <Box flex={1} marginRight='m'>
            {getProjectCard(item)}
          </Box>
        );
      }
      return <Box marginHorizontal='m'>{getProjectCard(item)}</Box>;
    },
    [alphabetize, alphabetical, getProjectCard]
  );

  const getCondensedBox = useCallback(
    (item: Project) => {
      if (!alphabetize && !alphabetical) {
        return getProjectCard(item);
      }
      return (
        <Box>
          <Box marginHorizontal='m' marginVertical='xs'>
            {getProjectCard(item)}
          </Box>
        </Box>
      );
    },
    [alphabetize, alphabetical, getProjectCard]
  );

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<Project>) => {
      if (index === 0) currentLetterRef.current = '';
      if (alphabetize) {
        return getCondensedBox(item);
      } else if (expanded) {
        return getExpandedBox(item);
      } else {
        if (
          item.name.charAt(0).toUpperCase() !== currentLetterRef.current &&
          alphabetical
        ) {
          currentLetterRef.current = item.name.charAt(0).toUpperCase();
          return (
            <Box>
              <Box
                justifyContent='center'
                paddingHorizontal='m'
                paddingVertical='xs'
                backgroundColor='grey01'>
                <Text variant='labelEmphasized'>
                  {currentLetterRef.current}
                </Text>
              </Box>
              <Box marginHorizontal='m' marginVertical='xs'>
                {getProjectCard(item)}
              </Box>
            </Box>
          );
        }
        return getCondensedBox(item);
      }
    },
    [
      alphabetize,
      expanded,
      alphabetical,
      getCondensedBox,
      getExpandedBox,
      getProjectCard,
    ]
  );

  if (loading && projects?.length < 1) return <ActivityIndicatorLoading />;

  const ListEmptyComponent = listEmptyComponent ?? EmptyStateNoProjects;

  return (
    <>
      {alphabetize && (
        <Box flex={1} position='relative'>
          <FlashList
            accessibilityLabel='Projects List'
            onEndReachedThreshold={onEndReachedThreshold}
            refreshing={refreshing}
            ref={flashListRef}
            refreshControl={refreshControl}
            keyboardDismissMode='on-drag'
            onEndReached={fetchMore}
            ListFooterComponent={<PaddingBottomListFooter />}
            data={sectionData}
            renderItem={({ item }) => {
              if (typeof item === 'string') {
                return (
                  <Box
                    backgroundColor='grey01'
                    paddingVertical='xs'
                    paddingLeft='m'>
                    <Text color='textPrimary' variant='labelEmphasized'>
                      {item}
                    </Text>
                  </Box>
                );
              } else {
                return renderItem({ item } as ListRenderItemInfo<Project>);
              }
            }}
            getItemType={(item) => {
              // To achieve better performance, specify the type based on the item
              return typeof item === 'string' ? 'sectionHeader' : 'row';
            }}
            estimatedItemSize={100}
          />
          <AlphaSortSlider
            flashListRef={flashListRef}
            alphabetizeList={alphabetizeList}
          />
        </Box>
      )}
      {!expanded && (
        <Box flex={1} alignContent='center' justifyContent='center'>
          {projects.length === 0 ? (
            <Box flex={1} justifyContent='center' alignItems='center'>
              <ListEmptyComponent />
            </Box>
          ) : (
            <FlashList
              accessibilityLabel='Projects List'
              refreshing={refreshing}
              keyboardDismissMode='on-drag'
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}
              data={projects}
              renderItem={renderItem}
              refreshControl={refreshControl}
              keyExtractor={(item) => item.id.toString()}
              onEndReachedThreshold={onEndReachedThreshold}
              onEndReached={fetchMore}
              estimatedItemSize={56}
              ListFooterComponent={
                <ProjectListWebFooter
                  hideMargin={!alphabetize && !alphabetical}
                />
              }
            />
          )}
        </Box>
      )}
      {expanded && (
        <ProjectCardGrid numColumns={4} list={projects} onPress={onClick} />
      )}
    </>
  );
};

export default ProjectList;
